import { useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCampaignInfo } from '../api';

/**
 * Click-to-load popover for the Location / CLI cells in the campaign list.
 *
 * The campaign list endpoint (`GET /api/obd/campaign/{userId}`) does NOT
 * carry per-row location or CLI data — legacy fetched it on demand from
 * `GET /api/obd/campaign/info/{campaignId}` only when the user clicked the
 * column's Location/CLI button. We mirror that here so the list endpoint
 * stays light and these columns only fetch on interaction.
 */
type Kind = 'location' | 'cli';

interface Props {
  campaignId: number;
  kind: Kind;
}

export function CampaignInfoPopover({ campaignId, kind }: Props) {
  const [open, setOpen] = useState(false);
  // Only fire the query once the popover opens. tanstack-query caches the
  // result for 30 s (see useCampaignInfo) so reopening is instant.
  const info = useCampaignInfo(open ? campaignId : null);

  const label = kind === 'location' ? 'Location' : 'CLI';
  const items = (() => {
    if (!info.data) return null;
    if (kind === 'location') {
      return (info.data.locationList ?? []).map((l, i) => ({
        key: `loc-${i}`,
        text: l.locationName ?? l.location ?? '—',
      }));
    }
    return (info.data.cliList ?? []).map((c, i) => ({
      key: `cli-${i}`,
      text: c.cli,
    }));
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 gap-1 px-2 text-xs"
        >
          {label}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        {info.isLoading ? (
          <div className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading…
          </div>
        ) : !items || items.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground">
            No {label} found
          </div>
        ) : (
          <ul className="max-h-56 overflow-y-auto py-1">
            {items.map((it) => (
              <li
                key={it.key}
                className="rounded px-2 py-1 text-xs text-foreground hover:bg-muted"
              >
                {it.text}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
